interface Series {
  id: string
  type: 'line' | 'column'
  name: string
  yAxis: any
  data: [number, number][]
}

type IndicatorType = 'series' | 'plotline' | 'label'

// For indicators of 'series' type, 'targetId' is the id of the very first
// series in the chain of transformations that resulted in this indicator.
// This means 'targetId' always points to a user-provided series.
// For other indicators, 'targetId' points to the actual series it is based on,
// which could be a user-provided series or another indicator.
interface Indicator {
  id: string
  name: string
  type: IndicatorType
  targetId: string
  steps: IndicatorStep[]
}

interface IndicatorMSDArgs {
  id: string
  subtype: 'msd'
  targetId: string
  name: string
}

interface IndicatorMinMaxArgs {
  id: string
  subtype: 'minmax'
  targetId: string
  name: string
}

interface IndicatorSMAArgs {
  id: string
  subtype: 'sma'
  targetId: string
  name: string
  period: number
}

interface IndicatorBase100Args {
  id: string
  subtype: 'base100'
  targetId: string
  name: string
}

interface IndicatorVariationArgs {
  id: string
  subtype: 'variation'
  targetId: string
  name: string
  period: number
  variationType: 'absolute' | 'relative'
}

type IndicatorMSDStep = Omit<IndicatorMSDArgs, 'id' | 'targetId' | 'name'>
type IndicatorMinMaxStep = Omit<IndicatorMinMaxArgs, 'id' | 'targetId' | 'name'>
type IndicatorSMAStep = Omit<IndicatorSMAArgs, 'id' | 'targetId' | 'name'>
type IndicatorBase100Step = Omit<
  IndicatorBase100Args,
  'id' | 'targetId' | 'name'
>
type IndicatorVariationStep = Omit<
  IndicatorVariationArgs,
  'id' | 'targetId' | 'name'
>

type IndicatorStep =
  | IndicatorMSDStep
  | IndicatorMinMaxStep
  | IndicatorSMAStep
  | IndicatorBase100Step
  | IndicatorVariationStep

export const reconstructSeries = (
  series: Series[],
  indicators: Indicator[]
) => {
  const reconstructedSeries = [...series]
  indicators
    .filter((indicator) => indicator.type === 'series')
    .forEach((indicator) => {
      const firstSeries = reconstructedSeries.find(
        (series) => series.id === indicator.targetId
      )

      if (!firstSeries) {
        throw new Error(
          `Tried to add indicator for series with id ${indicator.targetId}, but such series does not exist`
        )
      }

      let data = firstSeries.data as [number, number][]

      indicator.steps.forEach((step) => {
        if (step.subtype === 'sma') {
          data = computeSMA(data, step.period)
        } else if (step.subtype === 'base100') {
          data = computeBase100(data)
        } else if (step.subtype === 'variation') {
          data = computeVariation(data, step.period, step.variationType)
        } else {
          throw new Error(
            `Invalid step subtype for series indicator: ${step.subtype}`
          )
        }
      })

      reconstructedSeries.push({
        id: indicator.id,
        type: 'line',
        name: indicator.name,
        yAxis: firstSeries.yAxis,
        data,
      })
    })

  return reconstructedSeries
}

// Simple Moving Average
export const computeSMA = (data: [number, number][], period: number) => {
  const result: typeof data = []
  let sum = 0

  data.forEach(([x, y], index) => {
    if (index >= period) {
      sum -= data[index - period][1]
    }

    sum += y

    if (index >= period - 1) {
      result.push([x, sum / period])
    }
  })

  return result
}

export const computeBase100 = (data: [number, number][]) => {
  return data.map(([x, y]) => [x, (y / data[0][1]) * 100] as [number, number])
}

export const computeVariation = (
  data: [number, number][],
  period: number,
  type: 'absolute' | 'relative'
) => {
  return data
    .slice(period)
    .map(
      ([x, y], idx) =>
        (type === 'relative'
          ? [x, (100 * (y - data[idx][1])) / data[idx][1]]
          : [x, y - data[idx][1]]) as [number, number]
    )
}

export const computeMSDValues = (yValues: number[]): number[] => {
  const avg = yValues.reduce((a, b) => a + b, 0) / yValues.length
  const stdDev = Math.sqrt(
    yValues
      .map((value) => Math.pow(value - avg, 2))
      .reduce((a, b) => a + b, 0) / yValues.length
  )

  return [-2, -1, 0, 1, 2].map((idx) => avg + idx * stdDev)
}
